import { useCallback, useImperativeHandle, forwardRef, useRef } from "react";

import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";

const StyledTextarea = styled.textarea<{ $height?: string }>`
    && {
        width: 100%;
        height: ${({ $height }) => $height ?? "128px"};
        resize: none;
        border: none;
        background-color: ${colorsPalettes.carbon[0]};
        box-shadow: none;
        outline: none;
        margin: 0;
        padding: 0;

        ${setFontVariant("input2", colorsPalettes.carbon[400])}
        &::placeholder {
            color: ${colorsPalettes.carbon[200]};
        }
    }
`;

export interface ITextareaInputRef {
    updateLines: (lines: string[]) => void;
    getValue: () => string;
    focus: () => void;
}

interface ITextareaProps {
    placeholder?: string;
    height?: string;
    onTextChange: (value: string) => void;
}

export const TextareaInput = forwardRef<ITextareaInputRef, ITextareaProps>(({ onTextChange, placeholder, height }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
        focus: () => textareaRef.current?.focus(),
        updateLines: (lines: string[]) => {
            if (textareaRef.current) {
                const selectionStart = textareaRef.current.selectionStart;
                const selectionEnd = textareaRef.current.selectionEnd;
                const isFocused = document.activeElement === textareaRef.current;

                const currentLines = textareaRef.current.value.split(/\r?\n/);

                const areLinesSame =
                    currentLines.length === lines.length && currentLines.every((line, index) => line === lines[index]);

                if (areLinesSame) {
                    return;
                }

                textareaRef.current.value = lines.join("\n");
                if (isFocused) {
                    textareaRef.current.focus();
                    const newValue = textareaRef.current.value;
                    const newLength = newValue.length;

                    if (newLength >= selectionEnd) {
                        textareaRef.current.setSelectionRange(selectionStart, selectionEnd);
                    } else {
                        textareaRef.current.setSelectionRange(newLength, newLength);
                    }
                }
            }
        },
        getValue: () => textareaRef.current?.value || "",
    }));

    const handleChange = useCallback(() => {
        if (textareaRef.current) {
            onTextChange(textareaRef.current.value);
        }
    }, [onTextChange]);

    return (
        <StyledTextarea
            $height={height}
            data-automation="keywords-textarea"
            autoFocus
            ref={textareaRef}
            onChange={handleChange}
            placeholder={placeholder}
        />
    );
});
